import { useRef, useState } from 'react'
import {
  Collapse,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { canalFilterLabel, storesForChannel } from '../api/bridge'
import { Hicon } from './Hicon'

type CanalOption = { id: string; label: string }

type CanalFilterFieldProps = {
  value: string
  options: CanalOption[]
  onChange: (selectId: string) => void
  disabled?: boolean
  sx?: object
}

function parentChannelOf(value: string, options: CanalOption[]): string | null {
  if (!value) return null
  if (!value.startsWith('store:')) {
    return storesForChannel(value).length ? value : null
  }
  for (const o of options) {
    if (storesForChannel(o.id).some((s) => s.id === value)) return o.id
  }
  return null
}

/**
 * Hover no canal → expande lojas abaixo (só um aberto de cada vez).
 * Clique no canal ou na loja → selecciona e fecha o menu.
 * No touch: toque na seta expande; toque no nome selecciona.
 */
export function CanalFilterField({
  value,
  options,
  onChange,
  disabled,
  sx,
}: CanalFilterFieldProps) {
  const theme = useTheme()
  const isTouch = useMediaQuery(theme.breakpoints.down('md'))
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const leaveTimer = useRef<number | null>(null)

  const open = Boolean(anchorEl)
  const label = canalFilterLabel(value)

  const clearLeaveTimer = () => {
    if (leaveTimer.current != null) {
      window.clearTimeout(leaveTimer.current)
      leaveTimer.current = null
    }
  }

  const close = () => {
    clearLeaveTimer()
    setAnchorEl(null)
    setExpanded(null)
  }

  const pick = (id: string) => {
    onChange(id)
    close()
  }

  const openMenu = (el: HTMLElement) => {
    setAnchorEl(el)
    setExpanded(parentChannelOf(value, options))
  }

  const onChannelHover = (channelId: string | null) => {
    if (isTouch) return
    clearLeaveTimer()
    if (channelId && storesForChannel(channelId).length) {
      setExpanded(channelId)
    } else {
      // sem lojas (ou "Todos") — fecha o que estava aberto
      leaveTimer.current = window.setTimeout(() => setExpanded(null), 80)
    }
  }

  return (
    <>
      <TextField
        size="small"
        label="Canal"
        value={label}
        disabled={disabled}
        onClick={(e) => {
          if (disabled) return
          openMenu(e.currentTarget)
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <Hicon
              name="chevron-down"
              sx={{
                fontSize: '1.25rem',
                color: 'text.secondary',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s',
                pointerEvents: 'none',
              }}
            />
          ),
        }}
        inputProps={{
          'aria-haspopup': 'menu',
          'aria-expanded': open,
          style: { cursor: disabled ? 'default' : 'pointer' },
        }}
        sx={{
          minWidth: 160,
          cursor: disabled ? 'default' : 'pointer',
          '& .MuiInputBase-root': { cursor: disabled ? 'default' : 'pointer' },
          ...sx,
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
        MenuListProps={{
          dense: true,
          sx: { py: 0.5 },
          onMouseLeave: () => {
            if (isTouch) return
            leaveTimer.current = window.setTimeout(() => setExpanded(null), 160)
          },
        }}
      >
        {options.map((opt) => {
          const stores = storesForChannel(opt.id)
          const hasStores = stores.length > 0
          const isExpanded = expanded === opt.id
          const storeActive = stores.some((s) => s.id === value)
          const channelActive = value === opt.id

          return (
            <div
              key={opt.id || 'all'}
              onMouseEnter={() => onChannelHover(hasStores ? opt.id : null)}
            >
              <MenuItem
                selected={channelActive || storeActive}
                onClick={() => pick(opt.id)}
                sx={{ pr: hasStores ? 1 : 2 }}
              >
                <ListItemText primary={opt.label} />
                {hasStores ? (
                  <ListItemIcon
                    sx={{ minWidth: 28, justifyContent: 'flex-end' }}
                    onClick={
                      isTouch
                        ? (e) => {
                            e.stopPropagation()
                            setExpanded((prev) => (prev === opt.id ? null : opt.id))
                          }
                        : undefined
                    }
                  >
                    <Hicon
                      name="chevron-down"
                      sx={{
                        fontSize: '1.05rem',
                        color: 'text.secondary',
                        transform: isExpanded ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.32s ease',
                      }}
                    />
                  </ListItemIcon>
                ) : null}
              </MenuItem>

              {hasStores ? (
                <Collapse in={isExpanded} timeout={320} easing={{ enter: 'ease-out', exit: 'ease-in' }} unmountOnExit>
                  <div
                    onMouseEnter={() => {
                      clearLeaveTimer()
                      setExpanded(opt.id)
                    }}
                  >
                    {stores.map((s) => (
                      <MenuItem
                        key={s.id}
                        selected={value === s.id}
                        onClick={() => pick(s.id)}
                        sx={{
                          pl: 3.5,
                          py: 0.75,
                          typography: 'body2',
                          color: 'text.secondary',
                          '&.Mui-selected': { color: 'text.primary' },
                        }}
                      >
                        {s.label}
                      </MenuItem>
                    ))}
                  </div>
                </Collapse>
              ) : null}
            </div>
          )
        })}
      </Menu>
    </>
  )
}
